import {
  Button,
  Checkbox,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCoreRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import {
  Columns3,
  FilterX,
  MoreVertical,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AuthorizedButton from "@/components/common/AuthorizedButton";
import EdgeScrollButton from "@/components/common/EdgeScrollButton";
import PageHeader from "@/components/common/PageHeader";
import Paginator from "@/components/common/Paginator";
import SingleFieldEditDialog, {
  type SingleFieldTarget,
} from "@/components/common/SingleFieldEditDialog";
import ColumnSettingsDialog from "@/components/table/ColumnSettingsDialog";
import DataTable from "@/components/table/DataTable";
import { listPartners } from "@/features/partners/api";
import { previewProjectSetProduct } from "@/features/setProduct/api";
import SetProductDialog from "@/features/setProduct/SetProductDialog";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { useTableSettings } from "@/hooks/useTableSettings";
import { errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { canWrite } from "@/lib/authorization";
import { showSuccess } from "@/lib/toast";
import {
  deleteProjects,
  listProjects,
  setProjectSetProduct,
  updateProject,
  type Project,
  type ProjectInput,
} from "./api";
import {
  buildProjectColumns,
  projectColumnIds,
  projectDefaultHiddenColumnIds,
  projectPinnedColumnIds,
  type ProjectField,
} from "./columns";
import ProjectBulkDeleteDialog from "./ProjectBulkDeleteDialog";
import ProjectCreateDialog from "./ProjectCreateDialog";
import {
  PROJECTS_PAGE_SIZE,
  useProjectListParams,
  type ShippingFilter,
} from "./useProjectListParams";

const SHIPPING_OPTIONS: { key: ShippingFilter; label: string }[] = [
  { key: "all", label: "すべて" },
  { key: "unshipped", label: "未出荷" },
  { key: "shipped", label: "出荷済" },
];

/**
 * 案件一覧。検索条件は URL、表示列の設定は localStorage に持つ。
 *
 * 画面構成は見積一覧と同じ。金額が無い代わりに、納期・出荷予定日・出荷状況が
 * 主役になる。
 */
export default function ProjectList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const editable = canWrite(user, "transactional");
  const {
    params,
    update,
    reset: resetParams,
    shippedParam,
    unlinkedParam,
  } = useProjectListParams();
  const {
    settings,
    setColumnVisibility,
    setColumnOrder,
    setColumnSizing,
    reset: resetColumns,
  } = useTableSettings(
    "projects",
    projectColumnIds,
    projectPinnedColumnIds,
    projectDefaultHiddenColumnIds,
  );
  const { scrollRef, scrollBy, canScrollLeft, canScrollRight } = useHorizontalScroll();

  const [creating, setCreating] = useState(false);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  /** まとめて削除するために選んだ案件の id。 */
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  /** セット品の切り替えの確認ダイアログ（押した案件と切り替え先）。 */
  const [togglingSetProduct, setTogglingSetProduct] = useState<{
    project: Project;
    to: boolean;
  } | null>(null);
  /**
   * いま編集している 1 項目。
   *
   * ダイアログは一覧が 1 つだけ持つ。セルごとに Modal を抱えると、行数 × 列数
   * ぶんの Modal が描かれる（取引先一覧と同じ作り）。
   */
  const [editing, setEditing] = useState<{
    project: Project;
    field: ProjectField;
  } | null>(null);

  const query = useQuery({
    queryKey: ["projects", params],
    queryFn: () =>
      listProjects({
        keyword: params.keyword || undefined,
        shipped: shippedParam,
        unlinked: unlinkedParam,
        sortKey: params.sortKey,
        ascending: params.ascending,
        limit: PROJECTS_PAGE_SIZE,
        offset: (params.page - 1) * PROJECTS_PAGE_SIZE,
      }),
  });

  // 得意先は**取引中のものだけ**を選択肢に出す（取引を止めた相手を選ばせない）。
  // 編集ダイアログを開いたときに要るので、一覧と一緒に先に引いておく。
  const partnersQuery = useQuery({
    queryKey: ["partners", "picker"],
    queryFn: () =>
      listPartners({ disabled: false, isClient: true, limit: 500, offset: 0 }),
    enabled: editable,
  });

  // 直送先は用途が「直送先」の取引先だけ。得意先と同じマスタだが、候補に
  // 混ぜると送り先を取り違える。
  const shipTosQuery = useQuery({
    queryKey: ["partners", "shipTo", "picker"],
    queryFn: () =>
      listPartners({ disabled: false, isShipTo: true, limit: 500, offset: 0 }),
    enabled: editable,
  });

  const rows = query.data?.items ?? [];

  /**
   * 選ばれている案件。**いま画面に出ている行から引き直す**ので、消えた案件や
   * ページを移って見えなくなった案件の id が残っていても無視される。
   */
  const selected = rows.filter((p) => selectedIds.has(p.project_id));
  const allSelected = rows.length > 0 && selected.length === rows.length;

  // ページや検索条件を変えたら選択を捨てる。見えていない行を選んだまま削除
  // できると、何を消すのかが画面から読めなくなる。
  const paramsKey = JSON.stringify(params);
  useEffect(() => setSelectedIds(new Set()), [paramsKey]);

  const toggleSelect = (project: Project) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(project.project_id)) next.delete(project.project_id);
      else next.add(project.project_id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(rows.map((p) => p.project_id)));

  const removeMany = useMutation({
    mutationFn: (ids: number[]) => deleteProjects(ids),
    onSuccess: async (_data, ids) => {
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      setSelectedIds(new Set());
      setBulkDeleting(false);
      showSuccess(`案件 ${ids.length} 件を削除しました。`);
    },
    // 失敗の理由（出荷済みの案件など）はダイアログの中に出す。閉じてしまうと、
    // どれを選択から外せばよいか分からなくなる。
  });

  const columns = useMemo(
    () =>
      buildProjectColumns({
        canWrite: editable,
        selectedIds,
        onToggleSelect: toggleSelect,
        onEditField: (project, field) => setEditing({ project, field }),
        // スイッチは**明細の行数を変える**操作なので、確認ダイアログを通す。
        onToggleSetProduct: (project) =>
          setTogglingSetProduct({ project, to: !project.is_set_product }),
      }),
    // toggleSelect は毎回作り直されるが、中身は選択（selectedIds）だけに依存する。
    [editable, selectedIds],
  );

  /** 1 項目だけ保存する。エラー文言を返すとダイアログは開いたまま。 */
  const save = async (
    project: Project,
    input: ProjectInput,
  ): Promise<string | null> => {
    try {
      await updateProject(project.project_id, input);
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
      // 案件ヘッダを開いている最中に一覧から直すこともある。
      await queryClient.invalidateQueries({
        queryKey: ["project", project.project_id],
      });
      return null;
    } catch (e) {
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  /** 編集ダイアログに渡す設定。選択肢は画面側で引いた取引先から作る。 */
  const editTarget: SingleFieldTarget | null = editing
    ? {
        label: editing.field.label,
        value: editing.field.value(editing.project),
        kind: editing.field.kind ?? "text",
        description: editing.field.description,
        options:
          editing.field.options == null
            ? undefined
            : (
                (editing.field.options === "partners"
                  ? partnersQuery.data?.items
                  : shipTosQuery.data?.items) ?? []
              ).map((p) => ({
                key: String(p.partner_id),
                label: `${p.partner_code} ${p.partner_name}`,
              })),
      }
    : null;

  // 並び替えは API 側で行う（manualSorting）。列定義の meta.sortKey を使う。
  const sorting: SortingState = [{ id: params.sortKey, desc: !params.ascending }];

  const table = useReactTable<Project>({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.project_id),
    columnResizeMode: "onChange",
    manualSorting: true,
    manualPagination: true,
    enableSortingRemoval: false,
    state: {
      sorting,
      columnVisibility: settings.columnVisibility,
      columnOrder: settings.columnOrder,
      columnSizing: settings.columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      const first = next[0];
      if (!first) return;
      const sortKey = table.getColumn(first.id)?.columnDef.meta?.sortKey;
      if (!sortKey) return;
      update({ sortKey, ascending: !first.desc });
    },
    enableSorting: true,
    defaultColumn: { enableSorting: false, minSize: 60, maxSize: 600 },
  });

  // 列ごとの並び替え可否は meta.sortKey の有無で決まる。
  for (const column of table.getAllLeafColumns()) {
    column.columnDef.enableSorting = Boolean(column.columnDef.meta?.sortKey);
  }

  const total = query.data?.pagination.total ?? 0;

  return (
    <div className="relative h-full">
      <div className="flex h-full flex-col px-6 pt-3">
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-md">
          <PageHeader
            title="案件管理"
            actions={
              <>
                <Input
                  aria-label="工番・案件名・客先注文番号・客先購番・得意先名で検索"
                  placeholder="工番・案件名・注文番号・客先購番"
                  variant="bordered"
                  radius="sm"
                  className="w-64"
                  startContent={<Search size={16} className="text-default-400" />}
                  defaultValue={params.keyword}
                  onBlur={(e) =>
                    update({ keyword: (e.target as HTMLInputElement).value.trim() })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      update({ keyword: (e.target as HTMLInputElement).value.trim() });
                    }
                  }}
                  isClearable
                  onClear={() => update({ keyword: "" })}
                />
                <Select
                  aria-label="出荷状況で絞り込む"
                  variant="bordered"
                  radius="sm"
                  className="w-44"
                  disallowEmptySelection
                  selectedKeys={[params.shipping]}
                  onSelectionChange={(keys) => {
                    const value = [...keys][0] as ShippingFilter | undefined;
                    if (value) update({ shipping: value });
                  }}
                  renderValue={(items) => {
                    const key = items[0]?.key as ShippingFilter | undefined;
                    const label =
                      SHIPPING_OPTIONS.find((o) => o.key === key)?.label ?? "すべて";
                    return `出荷：${label}`;
                  }}
                  classNames={{
                    trigger: params.shipping !== "all" ? "border-brand-500" : "",
                    value: params.shipping !== "all" ? "font-bold text-brand-500" : "",
                  }}
                >
                  {SHIPPING_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
                {/* 特急案件の見積後付け漏れを拾うための絞り込み。件数を出す
                    だけでは、どの案件かを探すのに全件めくることになる
                    （[[urgent-project-flow]]）。 */}
                <Button
                  radius="sm"
                  variant={params.unlinkedOnly ? "solid" : "bordered"}
                  color={params.unlinkedOnly ? "warning" : "default"}
                  data-testid="filter-unlinked"
                  onPress={() => update({ unlinkedOnly: !params.unlinkedOnly })}
                >
                  見積未紐付のみ
                </Button>
                <AuthorizedButton
                  scope="transactional"
                  radius="full"
                  color="primary"
                  data-testid="project-create"
                  startContent={<Plus size={18} />}
                  onPress={() => setCreating(true)}
                >
                  新規案件
                </AuthorizedButton>

                <Dropdown>
                  <DropdownTrigger>
                    <Button
                      isIconOnly
                      variant="light"
                      radius="full"
                      aria-label="表示設定"
                    >
                      <MoreVertical size={20} className="text-brand-500" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="表示設定"
                    onAction={(key) => {
                      if (key === "columns") setColumnSettingsOpen(true);
                      if (key === "reload") void query.refetch();
                      if (key === "reset_filters") resetParams();
                      if (key === "reset_columns") resetColumns();
                    }}
                  >
                    <DropdownItem key="columns" startContent={<Columns3 size={18} />}>
                      表示列の設定
                    </DropdownItem>
                    <DropdownItem key="reload" startContent={<RefreshCw size={18} />}>
                      再読み込み
                    </DropdownItem>
                    <DropdownItem
                      key="reset_filters"
                      startContent={<FilterX size={18} />}
                    >
                      検索条件リセット
                    </DropdownItem>
                    <DropdownItem
                      key="reset_columns"
                      startContent={<Columns3 size={18} />}
                    >
                      表示列リセット
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </>
            }
          />

          {/* 選択と、選んだ案件への操作。**操作は選んでから**出す（全件に効く
              ボタンを常に置くと、押し間違えたときの被害が大きい）。
              「すべて選択」が効くのは**いま出ているページ**だけ。ページを
              またいで選べるようにすると、画面に無い案件まで消えてしまう。 */}
          {editable && (
            <div className="flex min-h-9 flex-wrap items-center gap-3 px-6 pb-2">
              <Checkbox
                size="sm"
                isSelected={allSelected}
                isIndeterminate={selected.length > 0 && !allSelected}
                isDisabled={rows.length === 0}
                onValueChange={toggleSelectAll}
                data-testid="project-select-all"
              >
                <span className="text-sm">このページをすべて選択</span>
              </Checkbox>
              {selected.length > 0 && (
                <>
                  <span className="text-sm font-bold text-brand-600">
                    {selected.length} 件を選択中
                  </span>
                  <AuthorizedButton
                    scope="transactional"
                    size="sm"
                    radius="full"
                    variant="light"
                    color="danger"
                    data-testid="project-bulk-delete"
                    startContent={<Trash2 size={16} />}
                    isLoading={removeMany.isPending}
                    onPress={() => setBulkDeleting(true)}
                  >
                    選んだ {selected.length} 件を削除
                  </AuthorizedButton>
                </>
              )}
            </div>
          )}

          <div className="min-h-0 flex-1">
            {query.isError ? (
              <div className="flex h-full flex-col items-center justify-center gap-4">
                <p className="text-default-600">
                  データの取得に失敗しました。
                  <br />
                  {(query.error as Error).message}
                </p>
                <Button color="primary" onPress={() => void query.refetch()}>
                  再読み込み
                </Button>
              </div>
            ) : query.isPending ? (
              <div className="flex h-full items-center justify-center">
                <Spinner label="読み込み中..." />
              </div>
            ) : (
              <DataTable
                table={table}
                isLoading={query.isFetching}
                emptyMessage="案件がありません"
                scrollRef={scrollRef}
                // 見積管理と枠が同じで見分けが付かないので、こちらはヘッダ行を
                // 塗る。詳細はタブ行で同じことをしている（[[ProjectDetail]]）。
                headerFilled
              />
            )}
          </div>
        </section>

        <Paginator
          total={total}
          pageSize={PROJECTS_PAGE_SIZE}
          page={params.page}
          onChange={(page) => update({ page })}
        />
      </div>

      <EdgeScrollButton
        side="left"
        isDisabled={!canScrollLeft}
        onPress={() => scrollBy(-1)}
      />
      <EdgeScrollButton
        side="right"
        isDisabled={!canScrollRight}
        onPress={() => scrollBy(1)}
      />

      <ColumnSettingsDialog
        isOpen={columnSettingsOpen}
        onClose={() => setColumnSettingsOpen(false)}
        table={table}
        onReset={resetColumns}
      />

      <SingleFieldEditDialog
        target={editTarget}
        onClose={() => setEditing(null)}
        onSave={(value) =>
          editing
            ? save(editing.project, editing.field.toInput(value))
            : Promise.resolve(null)
        }
      />

      {togglingSetProduct && (
        <SetProductDialog
          owner="project"
          number={togglingSetProduct.project.project_number}
          to={togglingSetProduct.to}
          previewKey={[
            "project-set-product-preview",
            togglingSetProduct.project.project_id,
            togglingSetProduct.to,
          ]}
          fetchPreview={() =>
            previewProjectSetProduct(
              togglingSetProduct.project.project_id,
              togglingSetProduct.to,
            )
          }
          onClose={() => setTogglingSetProduct(null)}
          onConfirm={async () => {
            const { project, to } = togglingSetProduct;
            await setProjectSetProduct(project.project_id, to);
            await queryClient.invalidateQueries({ queryKey: ["projects"] });
            await queryClient.invalidateQueries({
              queryKey: ["project", project.project_id],
            });
            setTogglingSetProduct(null);
            showSuccess(
              to
                ? `${project.project_number} をセット品にしました。`
                : `${project.project_number} のセット品を解除しました。`,
            );
          }}
        />
      )}
      <ProjectBulkDeleteDialog
        isOpen={bulkDeleting}
        projects={selected}
        onClose={() => setBulkDeleting(false)}
        onConfirm={() =>
          removeMany.mutateAsync(selected.map((p) => p.project_id)).then(() => {})
        }
      />

      <ProjectCreateDialog
        isOpen={creating}
        onClose={() => setCreating(false)}
        onCreated={(projects) => {
          void queryClient.invalidateQueries({ queryKey: ["projects"] });
          if (projects.length === 1) {
            showSuccess(`案件 ${projects[0].project_number} を起票しました。`);
            // 起票したら続けて明細を入れるため、そのまま詳細へ移動する。
            navigate(`/projects/${projects[0].project_id}`);
            return;
          }
          // **複数件（新東工業の CSV）は一覧に留まる。** どれを先に開くかは人が
          // 決めることで、勝手に 1 件へ飛ぶと残りを起票したことに気づけない。
          showSuccess(
            `${projects.length} 件の案件を起票しました（${projects
              .map((p) => p.project_number)
              .join("・")}）。`,
          );
        }}
      />
    </div>
  );
}
